import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, RefreshCw, Eye, MailCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '@/lib/utils';

interface User {
  id: string;
  email: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  user_metadata: {
    full_name?: string;
    role?: 'customer' | 'florist' | 'admin' | string;
  };
  app_metadata?: {
    provider?: string;
  };
  total_orders?: number;
  total_spent?: number;
  net_florist_payout?: number;
}

interface UserOrdersSummary {
  total_orders: number;
  total_spent: number;
  last_order_date: string | null;
}

interface CustomerSpending {
  customer_user_id: string;
  total_spent: number;
  total_orders: number;
}

interface FloristEarning {
  florist_user_id: string;
  total_order_value: number;
  platform_commission_total: number;
  net_florist_payout: number;
  total_orders_processed: number;
}

export default function UserManagement() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userOrdersSummary, setUserOrdersSummary] = useState<UserOrdersSummary | null>(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [customerSpendingData, setCustomerSpendingData] = useState<Record<string, Omit<CustomerSpending, 'customer_user_id'>>>({});
  const [floristEarningsData, setFloristEarningsData] = useState<Record<string, Omit<FloristEarning, 'florist_user_id'>>>({});
  const [sortConfig, setSortConfig] = useState<{ key: keyof User | 'amount'; direction: 'asc' | 'desc' }>({ key: 'created_at', direction: 'desc' });

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!user || !isAdmin) {
      navigate('/');
      return;
    }
    fetchUsersAndFinancials();
  }, [user, isAdmin, authLoading, navigate]);

  const fetchUsersAndFinancials = async () => {
    setLoading(true);
    try {
      const { data: rpcUserData, error: rpcUserError } = await supabase.rpc('get_all_users_admin');
      if (rpcUserError) throw rpcUserError;
      if (!rpcUserData) {
        setUsers([]); setLoading(false); toast.info("No user data available."); return;
      }
      const baseUsers: User[] = rpcUserData.map((rpcUser: any) => ({
        id: rpcUser.user_id,
        email: rpcUser.user_email,
        created_at: rpcUser.user_created_at,
        last_sign_in_at: rpcUser.user_last_sign_in_at,
        user_metadata: {
          full_name: rpcUser.display_name || rpcUser.user_metadata_payload?.full_name, // Use display_name from RPC
          role: rpcUser.role_extracted || rpcUser.user_metadata_payload?.role
        },
        app_metadata: { provider: rpcUser.user_metadata_payload?.provider || 'email' },
      }));

      const { data: spendingData, error: spendingError } = await supabase.rpc('get_customer_spending_summary');
      if (spendingError) console.error("Error fetching customer spending:", spendingError);
      const customerSpendingMap: Record<string, Omit<CustomerSpending, 'customer_user_id'>> = {};
      if (spendingData) {
        spendingData.forEach((item: CustomerSpending) => {
          customerSpendingMap[item.customer_user_id] = { total_spent: item.total_spent, total_orders: item.total_orders };
        });
      }
      setCustomerSpendingData(customerSpendingMap);

      const { data: earningsData, error: earningsError } = await supabase.rpc('get_florist_earnings_summary');
      if (earningsError) console.error("Error fetching florist earnings:", earningsError);
      const floristEarningsMap: Record<string, Omit<FloristEarning, 'florist_user_id'>> = {};
      if (earningsData) {
        await Promise.all(
          earningsData.map(async (item: FloristEarning) => {
            floristEarningsMap[item.florist_user_id] = {
              total_order_value: item.total_order_value,
              platform_commission_total: item.platform_commission_total,
              net_florist_payout: item.net_florist_payout,
              total_orders_processed: item.total_orders_processed,
            };
          })
        );
      }
      setFloristEarningsData(floristEarningsMap);
      
      setUsers(baseUsers);
    } catch (error: any) {
      toast.error(`Failed to load data: ${error.message}`);
      console.error('Error fetching users or financials:', error);
      setUsers([]); setCustomerSpendingData({}); setFloristEarningsData({});
    } finally {
      setLoading(false);
    }
  };

  const processedAndFilteredUsers = useMemo(() => {
    let enrichedUsers = users.map(u => {
      if (u.user_metadata?.role === 'customer' && customerSpendingData[u.id]) {
        return { 
          ...u, 
          total_spent: customerSpendingData[u.id].total_spent,
          total_orders: customerSpendingData[u.id].total_orders
        };
      }
      if (u.user_metadata?.role === 'florist' && floristEarningsData[u.id]) {
        return { 
          ...u, 
          net_florist_payout: floristEarningsData[u.id].net_florist_payout,
          total_orders: floristEarningsData[u.id].total_orders_processed
        };
      }
      return u;
    });

    if (searchTerm) {
      const lowercaseSearch = searchTerm.toLowerCase();
      enrichedUsers = enrichedUsers.filter(
        (u) =>
          u.email?.toLowerCase().includes(lowercaseSearch) ||
          u.user_metadata?.full_name?.toLowerCase().includes(lowercaseSearch) ||
          u.id.toLowerCase().includes(lowercaseSearch)
      );
    }
    
    if (roleFilter !== 'all') {
      enrichedUsers = enrichedUsers.filter((u) => u.user_metadata?.role === roleFilter);
    }
    
    enrichedUsers.sort((a, b) => {
      let valA: any;
      let valB: any;

      if (sortConfig.key === 'amount') {
        valA = a.user_metadata?.role === 'customer' ? a.total_spent : a.net_florist_payout;
        valB = b.user_metadata?.role === 'customer' ? b.total_spent : b.net_florist_payout;
        valA = valA === undefined || valA === null ? (sortConfig.direction === 'asc' ? Infinity : -Infinity) : valA;
        valB = valB === undefined || valB === null ? (sortConfig.direction === 'asc' ? Infinity : -Infinity) : valB;
      } else if (sortConfig.key === 'created_at' || sortConfig.key === 'last_sign_in_at') {
        valA = a[sortConfig.key] ? new Date(a[sortConfig.key]!).getTime() : (sortConfig.direction === 'asc' ? Infinity : -Infinity) ;
        valB = b[sortConfig.key] ? new Date(b[sortConfig.key]!).getTime() : (sortConfig.direction === 'asc' ? Infinity : -Infinity) ;
      } else {
        valA = (a as any)[sortConfig.key]; 
        valB = (b as any)[sortConfig.key];
        valA = valA === undefined || valA === null ? '' : String(valA).toLowerCase();
        valB = valB === undefined || valB === null ? '' : String(valB).toLowerCase();
      }

      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return enrichedUsers;
  }, [users, customerSpendingData, floristEarningsData, searchTerm, roleFilter, sortConfig]);

  const requestSort = (key: keyof User | 'amount') => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const fetchUserOrdersSummary = async (userId: string) => {
    try {
      setLoadingUserDetails(true);
      
      const { data, error } = await supabase
        .from('orders')
        .select('id, total_amount, created_at')
        .eq('customer_id', userId)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      if (data && data.length > 0) {
        const totalSpent = data.reduce((sum, order) => sum + (order.total_amount || 0), 0);
        
        setUserOrdersSummary({
          total_orders: data.length,
          total_spent: totalSpent,
          last_order_date: data[0]?.created_at || null
        });
      } else {
        setUserOrdersSummary({
          total_orders: 0,
          total_spent: 0,
          last_order_date: null
        });
      }
    } catch (error) {
      console.error('Error fetching user orders:', error);
      toast.error('Failed to load user order information');
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    fetchUserOrdersSummary(user.id);
  };

  const handleResetPassword = async () => {
    try {
      setIsResettingPassword(true);
      
      const { error } = await supabase.auth.resetPasswordForEmail(resetPasswordEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) throw error;
      
      toast.success('Password reset email sent successfully');
      setResetPasswordDialogOpen(false);
    } catch (error: any) {
      console.error('Error sending reset password email:', error);
      toast.error(error.message || 'Failed to send reset password email');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const initiatePasswordReset = (email: string) => {
    setResetPasswordEmail(email);
    setResetPasswordDialogOpen(true);
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-purple-600">Admin</Badge>;
      case 'florist':
        return <Badge className="bg-indigo-600">Florist</Badge>;
      case 'customer':
        return <Badge className="bg-emerald-600">Customer</Badge>;
      default:
        return <Badge className="bg-gray-500">Unknown</Badge>;
    }
  };

  const SortIndicator = ({ columnKey }: { columnKey: keyof User | 'amount' }) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽';
    }
    return null;
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
            <p className="text-gray-600">View and manage all users in the system</p>
          </div>
          <Button 
            onClick={fetchUsersAndFinancials} 
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Total Users</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : users.length}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Customers</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  users.filter(u => u.user_metadata?.role === 'customer').length
                )}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Florists</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  users.filter(u => u.user_metadata?.role === 'florist').length
                )}
              </p>
            </CardContent>
          </Card>
        </div>
        
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Search users..."
                  className="pl-8 w-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <Select
                value={roleFilter}
                onValueChange={setRoleFilter}
              >
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="customer">Customers</SelectItem>
                  <SelectItem value="florist">Florists</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => requestSort('created_at')}                    
                    >
                      Joined
                      <SortIndicator columnKey="created_at" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => requestSort('last_sign_in_at')}
                    >
                      Last Login
                      <SortIndicator columnKey="last_sign_in_at" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50 text-right"
                      onClick={() => requestSort('total_orders')}
                    >
                      Total Orders
                      <SortIndicator columnKey="total_orders" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50 text-right"
                      onClick={() => requestSort('amount')}
                    >
                      Amount (Spent/Earned)
                      <SortIndicator columnKey="amount" />
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {processedAndFilteredUsers.length > 0 ? (
                    processedAndFilteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-gray-800">
                              {user.user_metadata?.full_name || 'Not specified'}
                            </p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getRoleBadge(user.user_metadata?.role || 'unknown')}
                        </TableCell>
                        <TableCell>
                          {user.created_at && typeof user.created_at === 'string' 
                            ? formatDate(user.created_at) 
                            : 'Invalid Date'} 
                        </TableCell>
                        <TableCell>
                          {user.last_sign_in_at && typeof user.last_sign_in_at === 'string'
                            ? formatDate(user.last_sign_in_at)
                            : 'Never'
                          }
                        </TableCell>
                        <TableCell className="text-right">
                          {user.total_orders !== undefined ? user.total_orders : 'N/A'}
                        </TableCell>
                        <TableCell className="text-right">
                          {user.user_metadata?.role === 'customer' && user.total_spent !== undefined 
                            ? formatCurrency(user.total_spent)
                            : user.user_metadata?.role === 'florist' && user.net_florist_payout !== undefined
                            ? formatCurrency(user.net_florist_payout)
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleUserSelect(user)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => initiatePasswordReset(user.email || '')}>
                              <MailCheck className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No users found matching your criteria
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        {/* User Details Dialog */}
        {selectedUser && (
          <Dialog open={!!selectedUser} onOpenChange={(open) => !open && setSelectedUser(null)}>
            <DialogContent className="sm:max-w-[625px]">
              <DialogHeader>
                <DialogTitle>User Details</DialogTitle>
                <DialogDescription>
                  Detailed information for {selectedUser.user_metadata?.full_name || selectedUser.email}.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">User Information</h3>
                    <div className="mt-2 space-y-2">
                      <div><p className="text-sm font-medium text-gray-500">Name</p><p>{selectedUser.user_metadata?.full_name || 'Not specified'}</p></div>
                      <div><p className="text-sm font-medium text-gray-500">Email</p><p>{selectedUser.email}</p></div>
                      <div><p className="text-sm font-medium text-gray-500">Role</p><p className="capitalize">{selectedUser.user_metadata?.role || 'Unknown'}</p></div>
                      <div><p className="text-sm font-medium text-gray-500">User ID</p><p className="text-xs">{selectedUser.id}</p></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Activity Information</h3>
                    <div className="mt-2 space-y-2">
                      <div><p className="text-sm font-medium text-gray-500">Joined</p><p>{formatDate(selectedUser.created_at)}</p></div>
                      <div><p className="text-sm font-medium text-gray-500">Last Sign In</p><p>{selectedUser.last_sign_in_at ? formatDate(selectedUser.last_sign_in_at) : 'Never'}</p></div>
                      <div><p className="text-sm font-medium text-gray-500">Sign In Method</p><p className="capitalize">{selectedUser.app_metadata?.provider || 'Email'}</p></div>
                    </div>
                  </div>
                </div>
                
                {(selectedUser.user_metadata?.role === 'customer' || selectedUser.user_metadata?.role === 'florist') && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      {selectedUser.user_metadata?.role === 'customer' ? 'Customer Financials' : 'Florist Financials'}
                    </h3>
                    {loadingUserDetails ? (
                      <div className="flex items-center justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
                    ) : (
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-3 rounded">
                          <p className="text-sm font-medium text-gray-500">Total Orders</p>
                          <p className="text-xl font-semibold">
                            {selectedUser.total_orders !== undefined ? selectedUser.total_orders : userOrdersSummary?.total_orders ?? 'N/A'}
                          </p>
                        </div>
                        {selectedUser.user_metadata?.role === 'customer' && (
                          <div className="bg-gray-50 p-3 rounded">
                            <p className="text-sm font-medium text-gray-500">Total Spent</p>
                            <p className="text-xl font-semibold">
                              {selectedUser.total_spent !== undefined ? formatCurrency(selectedUser.total_spent) : userOrdersSummary?.total_spent !== undefined ? formatCurrency(userOrdersSummary.total_spent) : 'N/A'}
                            </p>
                          </div>
                        )}
                        {selectedUser.user_metadata?.role === 'florist' && (
                          <>
                            <div className="bg-gray-50 p-3 rounded">
                              <p className="text-sm font-medium text-gray-500">Net Payout</p>
                              <p className="text-xl font-semibold">
                                {selectedUser.net_florist_payout !== undefined ? formatCurrency(selectedUser.net_florist_payout) : 'N/A'}
                              </p>
                            </div>
                          </>
                        )}
                        {/* Fallback for customer last order date if not directly on selectedUser */}
                        {selectedUser.user_metadata?.role === 'customer' && userOrdersSummary?.last_order_date && (
                           <div className="bg-gray-50 p-3 rounded md:col-span-2">
                             <p className="text-sm font-medium text-gray-500">Last Order Date</p>
                             <p className="text-xl font-semibold">{formatDate(userOrdersSummary.last_order_date)}</p>
                           </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => initiatePasswordReset(selectedUser.email || '')}
                  className="flex items-center gap-2"
                >
                  <MailCheck className="h-4 w-4" />
                  Reset Password
                </Button>
                <Button onClick={() => setSelectedUser(null)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset User Password</DialogTitle>
              <DialogDescription>
                This will send a password reset email to the user.
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-md">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <p className="text-sm text-amber-700">
                A password reset link will be sent to: <strong>{resetPasswordEmail}</strong>
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={isResettingPassword}
              >
                {isResettingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Reset Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
    </AdminLayout>
  );
} 